import { ForwardedRef, useImperativeHandle, useState } from "react";
import Image from "next/image";
import useAnimate from "@/hooks/useAnimate";
import { quad } from "@/utils/motion";
import React from "react";
import getFullWebCdnUrl from "@/utils/getFullWebCdnUrl";

interface WheelProps {
  prizeIndex?: number;
  onStart?: () => void;
  onEnd?: (index: number) => void;
}

interface WheelRef {
  play: () => void;
  stop: (index: number) => void;
}

enum StepStatus {
  STOP = 0, // 未开始|| 结束
  UP = 1, // 加速
  CONSTANT = 2, // 匀速
  DOWN = 3, // 减速
}

// config
const ACCELERATION_TIME = 2500;
const DECELERATION_TIME = 5000;
const SPEED = 20;
const PRIZE_DEG = 360 / 6;

let FPS = 16.6;
let step = 0;

let startTime = 0;
let endTime = 0;

let rotateDeg = 0;

let stopDeg = 0; // 开始减速时的角度
let endDeg = 0; // 停止角度

let prizeIndex: number | undefined = undefined;

function RFWheel({ onStart, onEnd }: WheelProps, ref: ForwardedRef<WheelRef>) {
  useImperativeHandle(ref, () => ({
    play,
    stop,
  }));

  const [uiRotateDeg, setUiRotateDeg] = useState(0);
  const animate = useAnimate();

  const play = () => {
    prizeIndex = undefined;
    startTime = Date.now();
    step = StepStatus.UP;
    onStart && onStart();
    run();
  };

  const run = (rotateNum = 0) => {
    // 结束
    if (step === StepStatus.STOP) {
      onEnd && onEnd(prizeIndex as number);
      return;
    }

    // 异常
    if (prizeIndex === -1) {
      return;
    }

    // 设置结束为止
    if (step === StepStatus.DOWN && !endDeg) {
      handelGetEndDeg();
    }

    const startInterval = Date.now() - startTime;
    const endInterval = Date.now() - endTime;
    let _rotateDeg = rotateDeg;

    // 加速
    if (step === StepStatus.UP || startInterval < ACCELERATION_TIME) {
      FPS = startInterval / rotateNum;
      const speed = quad.easeIn(startInterval, 0, SPEED, ACCELERATION_TIME);
      //  最大值 改为匀速
      if (speed === SPEED) {
        step = StepStatus.CONSTANT;
      }
      _rotateDeg = _rotateDeg + (speed % 360);

      // 均速
    } else if (step === StepStatus.CONSTANT) {
      _rotateDeg = _rotateDeg + (SPEED % 360);
      // 如果server 获取结果
      if (prizeIndex !== undefined && prizeIndex !== -1) {
        step = StepStatus.DOWN;
        // 重置
        stopDeg = 0;
        endDeg = 0;
      }

      // 减速
    } else if (step === StepStatus.DOWN) {
      _rotateDeg = quad.easeOut(
        endInterval,
        stopDeg,
        endDeg,
        DECELERATION_TIME
      );

      if (endInterval >= DECELERATION_TIME) {
        step = StepStatus.STOP;
      }

      // 异常
    } else {
      stop(-1);
    }

    rotateDeg = _rotateDeg;
    setUiRotateDeg(_rotateDeg);

    animate(() => {
      run(rotateNum + 1);
    });
  };

  const stop = (index: number) => {
    if (step === StepStatus.STOP || step === StepStatus.DOWN) {
      return;
    }

    if (index < 0) {
      step = StepStatus.STOP;
      prizeIndex = -1;
    } else {
      step = StepStatus.CONSTANT;
      prizeIndex = index;
    }
  };

  const handelGetEndDeg = () => {
    endTime = Date.now();
    stopDeg = rotateDeg;
    const _stopDeg = rotateDeg;

    let i = 0;
    let pSpeed = 0;
    let pDeg = 0;
    while (++i) {
      let _endDeg = 360 * i - Number(prizeIndex) * PRIZE_DEG - _stopDeg;

      const currentSpeed =
        quad.easeOut(FPS, _stopDeg, _endDeg, DECELERATION_TIME) - _stopDeg;

      if (currentSpeed > SPEED) {
        endDeg = SPEED - pSpeed > currentSpeed - SPEED ? _endDeg : pDeg;
        break;
      }

      pSpeed = currentSpeed;
      pDeg = _endDeg;
    }
  };

  return <>
    <div
      className="wrapper"
      style={{ transform: `rotate3d(0, 0, 1, ${uiRotateDeg}deg)` }}
    >
      {/* remove webp 模糊 */}
      <Image
        src="https://web-cdn.rockflow.tech/h5/pulling-new/wheel-bg-2v2.png"
        alt="wheel"
        width={268}
        height={268}
        priority={true}
        sizes="100vw"
        style={{
          width: "100%",
          height: "auto"
        }} />
    </div>

    <style jsx>{`
      .wrapper {
        width: 100%;
        box-size: border-box;
        position: relative;
        transform: rotate3d(0, 0, 1, 0deg);
      }
      .wrapper img {
        width: 100%;
        display: block;
        border: none;
        outline: none;
      }
    `}</style>
  </>;
}

export default React.forwardRef(RFWheel);
